/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { ProviderTypeDTO } from '@coop/atacama-codegen';
export const COOKIE_NAME = '2fa';
export const DEVICE_COOKIE_MAX_AGE = 2.592e9; // 30 days in ms
export const QH_ASSETS = 'https://s3.amazonaws.com/qh-assets-public';

export const SUPPORT_PHONE_NUMBER = {
  humanReadable: '(877) 258-4010',
  htmlFormatted: 'tel:+18772584010'
};

export const HEALTHY_BLUE_CRISIS_PHONE_NUMBER = {
  humanReadable: '(884)-594-5076',
  htmlFormatted: 'tel:18445945076'
};

export const QUARTET_MEDICAL_MAIN_LINE = {
  humanReadable: '(833) 258-8498',
  htmlFormatted: 'tel:18332588498'
};

// This crisis phone number is specific to quartet
// and should not be made public.
export const QUARTET_ONLY_CRISIS_PHONE_NUMBER = {
  humanReadable: '(866)-694-3401',
  htmlFormatted: 'tel:8666943401'
};

export const CRISIS_PHONE_NUMBER = {
  humanReadable: '(800)-273-8255',
  htmlFormatted: 'tel:8002738255'
};

export const PATIENT_PASSWORD_RESET_PATH = '/p/password-reset/';
export const CARETEAM_PASSWORD_RESET_PATH = '/account/password-reset/';

export const NOTICE_OF_NONDISCRIMINATION =
  'https://www.quartethealth.com/discrimination-agreement/';
export const TERMS_AND_CONDITIONS =
  'https://www.quartethealth.com/terms-of-service/';
export const DIGITAL_CARE_LINK =
  'https://www.quartethealth.com/get-care/nc/digital-care-partners/';
export const TOS_VERSION_100 = '1.0.0';
export const TOS_VERSION_101 = '1.0.1';
export const BHP_TOS_VERSION = TOS_VERSION_100;
export const PATIENT_TOS_VERSION = TOS_VERSION_100;
export const PRIVACY_POLICY = 'https://www.quartethealth.com/privacy-policy/';
export const PAPER_ASSESSMENT_URI =
  'https://qh-public.s3.amazonaws.com/www/static/pdfs/assessments/QH_Assessment_r.pdf';
export const FOR_PROVIDERS_MARKETING_URL = 'https://www.quartethealth.com/';
export const MENTAL_HEALTH_PROVIDERS_FORM =
  'https://www.quartethealth.com/mental-health-providers/#form';
export const MENTAL_HEALTH_GET_CARE_RESOURCES =
  'https://www.quartethealth.com/get-care/resources/';
export const PRACTICE_ADMIN_REQUIRED_ERROR =
  'Deactivation/Role Change Failed: Your practice must have at least 1 active Administrator';

export const PASSWORD_RESET_EXPIRED_ERROR_MESSAGE = `The password reset link you are using has expired`;
export const USER_FACING_ERROR_MESSAGE = `We're sorry, something went wrong. Please try again later.`;

export const PCP_NAMESPACE = 'pcp.exp';
export const LAST_30_DAYS = 'LAST_30_DAYS';
export const LAST_60_DAYS = 'LAST_60_DAYS';
export const LAST_90_DAYS = 'LAST_90_DAYS';
export const LAST_YEAR = 'LAST_YEAR';
export const NULL_URL_PARAM = '0';
export const BEGINNING_OF_ALL_ACTIVITY = NULL_URL_PARAM;

export const ALL_ACTIVITY = 'All activity';

export const ActivityOptions = {
  LAST_30_DAYS: 'Last 30 days',
  LAST_60_DAYS: 'Last 60 days',
  LAST_90_DAYS: 'Last 90 days'
};

export const CREATION_CHANNEL_PCP = 'serviceRequest.creationChannel/PCP';
export const NOTIFICATIONS_RELEASE_DATE = '2017-07-01T00:00:00Z';
export const CLAIMS_SIZE = 12;

export const Icons = {
  PhoneIcon: 'PhoneIcon',
  AppointmentCheckMarkIcon: 'AppointmentCheckMarkIcon',
  AppointmentMessageBoxIcon: 'AppointmentMessageBoxIcon'
};

export const ERROR_MESSAGES = {
  NO_ASSESSMENT_FOUND: `We had a little trouble finding this page. Please try again. If this continues, please contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}.`,
  NO_ASSESSMENT_ADMIN:
    'We had a little trouble loading this page. If this continues, please flag in the App Bugs in Asana.',
  DEFAULT_ASSESSMENT_ERROR: `We had a little trouble loading this page. If this continues, please contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}.`,
  NO_PERMISSIONS_PATIENT: `It looks like you don't have the permissions to view this page. If  you believe that this is in error, please contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}.`,
  NO_PERMISSION_PCP:
    "It looks like you don't have the permissions to view this page. Please ask your practice administrator to change your permissions.",
  NO_PERMISSION_BHP: `It looks like you don't have the permissions to view this page. Please confirm that this patient is in your care. If this continues, please contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}`,
  PCP_EMAIL_EXISTS:
    'This email address is already associated with a Quartet user.',
  REFERRAL_SUBMIT_ERROR: `We're sorry. Something went wrong. Please try again and contact us at ${SUPPORT_PHONE_NUMBER.humanReadable} if you're still having trouble.`,
  REFERRAL_INVALID_EMAIL: 'Enter valid email address'
};

export const TOOLTIP_MESSAGES = {
  EMAIL_FIELD_IS_DISABLED: `To change the email address listed, contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}.`,
  NPI_FIELD_IS_DISABLED: `To change the NPI number listed, contact us at ${SUPPORT_PHONE_NUMBER.humanReadable}.`,
  ADMIN_SWITCH_IS_DISABLED:
    'You cannot revoke your own administrator privileges.'
};

export enum EVENT_ACTION_TYPES {
  ACKNOWLEDGED = 'app.coop.event.action.type/acknowledged',
  CONFIRMED = 'app.coop.event.action.type/confirmed'
}

export const EVENT_ACTION_TYPE_LABELS = {
  [EVENT_ACTION_TYPES.ACKNOWLEDGED]: 'Acknowledged',
  [EVENT_ACTION_TYPES.CONFIRMED]: 'Confirmed'
};

// Enum to be updated for any future feature intro modal content.
// See the FeatureIntroModal component.
export enum FEATURE_INTRO_TYPES {
  MHP_SESSION_TIMEOUT = 'mhpSessionTimeout' // updating session timeout to 15 minutes of inactivity
}

export const ALERT_MESSAGES = {
  UPDATE_REQUESTED: "Update patient's contact information",
  ACKNOWLEDGE: 'Acknowledge this important update',
  ACKNOWLEDGE_NON_IMPORTANT: 'Acknowledge this update',
  IS_WAITING: 'This is an important update awaiting acknowledgement',
  IS_OWN_WAITING: 'You sent an important update awaiting acknowledgement',
  IS_IMPORTANT: 'This is an important update'
};

export const PROVIDER_TYPE_TO_ROLE: { [key in ProviderTypeDTO]: string } = {
  BHP: 'person.role/behavioralHealthProvider',
  BH_ADMIN: 'person.role/behavioralHealthProvider',
  PCP: 'person.role/medicalProvider'
};

export const PROVIDER_PROGRESS_UPDATE_CARE_EVENTS = [
  'BHP_INITIAL_PROGRESS_UPDATE',
  'BHP_LEGACY_PROGRESS_UPDATE',
  'BHP_STRUCTURED_PROGRESS_UPDATE',
  'CASE_MANAGER_PROGRESS_UPDATE',
  'PCP_PROGRESS_UPDATE'
];

// The ETL is using this hardcoded uuid to backfill
// missing Author on Quartet notes
export const BACKFILL_PROVIDER_ID = '5babbfac-d0bf-473c-842b-e83d12b90bb1';

export const INVALID_SESSION_TYPE_APPLICATION = 'application';
export const INVALID_SESSION_TYPE_APPLICATION_MSG =
  'You have been logged out of Quartet. Please log back in to use our latest features.';
export const INVALID_SESSION_TYPE_SESSION = 'session';
export const INVALID_SESSION_TYPE_SESSION_MSG =
  'You have been logged out due to inactivity';
export const INVALID_SESSION_TYPE_INACTIVITY = 'inactivity';
export const INVALID_SESSION_TYPE_INACTIVITY_MSG =
  'You have been logged out due to inactivity';
export const SESSION_REFRESH_MSG_TEMPLATE =
  'You will be logged out in %time% %type%';
export const SESSION_REFRESH_MSG_ACTION =
  'Click here or anywhere on this page to extend your current login';
export const SESSION_RELOAD_BROWSER_MSG =
  'You need to refresh the site to continue using Quartet.';
export const SESSION_RELOAD_BROWSER_MSG_ACTION = 'Click here to continue.';
export const INACTIVE_ACCOUNT_LOGIN_ERROR =
  'Your account has been deactivated. For help, contact your practice administrator or contact us at';
export const INVALID_USERNAME_PASSWORD_LOGIN_ERROR =
  'Invalid username and/or password. Try again.';
export const INVALID_USERNAME_PASSWORD_LOGIN_LOCKOUT =
  'You have exceeded 5 failed login attempts for this account. Try again in 15 minutes or email';
export const INVALID_SSO_LOGIN_ERROR =
  'Your account must login through your company login page';

export const REQUEST_ID_HEADER: string = 'x-request-id';

export const FACEBOOK = 'https://www.facebook.com/quartethealth';
export const TWITTER = 'https://twitter.com/quartethealth';
export const LINKEDIN = 'https://www.linkedin.com/company/9224794';
export const INSTAGRAM = 'https://www.instagram.com/quartethealth/';
export const SECURITY = 'https://www.quartethealth.com/security/';

export const NoticeOfNondiscriminationURL =
  'https://www.quartethealth.com/discrimination-agreement/';

export const instructionsURL =
  'https://qh-public.s3.amazonaws.com/www/media/docs/VBC+Data+Dictionary+-+User+Instructions+V1.9.xlsx';

export const qmBookingsURL =
  'https://outlook.office365.com/book/QuartetMedicalGroup@quartethealth.com/';
